import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ConnectAnimation } from './ConnectAnimation';
import { SecurityProxy } from './SecurityProxy';
import { ChatExamples } from './ChatExamples';
import { MemoryCenter } from './MemoryCenter';
import { ChatManagement } from './ChatManagement';
import { ConnectionSetup } from './ConnectionSetup';
import { RecapsFeed } from './RecapsFeed';
import { DoAnythingCarousel } from './DoAnythingCarousel';
import { TIMELINE, lerp } from '../scrollTimeline';
export function Hero() {
  const containerRef = useRef<HTMLDivElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: containerRef,
    offset: ['start start', 'end end']
  });
  // === INTRO ===
  const title1Y = useTransform(scrollYProgress, [TIMELINE.intro.start, TIMELINE.intro.end], ['28vh', '15vh']);
  const title1Scale = useTransform(scrollYProgress, [TIMELINE.intro.start, TIMELINE.intro.end], [1, 0.6]);
  const title2IntroY = useTransform(scrollYProgress, [TIMELINE.intro.start, TIMELINE.intro.end], ['38vh', '48vh']);
  const title2IntroOpacity = useTransform(scrollYProgress, [TIMELINE.intro.start, lerp(TIMELINE.intro.start, TIMELINE.intro.end, 0.66)], [1, 0]);
  const title3IntroY = useTransform(scrollYProgress, [TIMELINE.intro.start, TIMELINE.intro.end], ['48vh', '58vh']);
  const title3IntroOpacity = useTransform(scrollYProgress, [TIMELINE.intro.start, lerp(TIMELINE.intro.start, TIMELINE.intro.end, 0.66)], [1, 0]);
  // === SCROLL HINT CHEVRON ===
  const chevronOpacity = useTransform(scrollYProgress, [TIMELINE.intro.start, lerp(TIMELINE.intro.start, TIMELINE.intro.end, 0.33)], [1, 0]);
  // === CONTENT 1A: Brain Animation ===
  const content1Y = useTransform(scrollYProgress, [TIMELINE.intro.start, TIMELINE.intro.end, TIMELINE.brain.end], ['100vh', '30vh', '12vh']);
  const content1Opacity = useTransform(scrollYProgress, [lerp(TIMELINE.intro.start, TIMELINE.brain.end, 0.13), TIMELINE.intro.end, lerp(TIMELINE.intro.start, TIMELINE.brain.end, 0.86), TIMELINE.connectionSetup.slideIn[1]], [0, 1, 1, 0]);
  const subtitleOpacity = useTransform(scrollYProgress, [lerp(TIMELINE.intro.start, TIMELINE.brain.end, 0.26), lerp(TIMELINE.intro.start, TIMELINE.brain.end, 0.53), lerp(TIMELINE.intro.start, TIMELINE.brain.end, 0.86), TIMELINE.connectionSetup.slideIn[1]], [0, 1, 1, 0]);
  const subtitleY = useTransform(scrollYProgress, [lerp(TIMELINE.intro.start, TIMELINE.brain.end, 0.26), lerp(TIMELINE.intro.start, TIMELINE.brain.end, 0.53)], ['24vh', '21vh']);
  // === CONTENT 1B: Product GIF ===
  const securityY = useTransform(scrollYProgress, [TIMELINE.connectionSetup.slideIn[0], TIMELINE.connectionSetup.slideIn[1], TIMELINE.connectionSetup.visible[1] - 0.05], ['80vh', '27vh', '27vh']);
  const securityOpacity = useTransform(scrollYProgress, [TIMELINE.connectionSetup.visible[0], TIMELINE.connectionSetup.visible[0] + 0.03, TIMELINE.connectionSetup.visible[1] - 0.02, TIMELINE.connectionSetup.visible[1]], [0, 1, 1, 0]);
  const securityPointerEvents = useTransform(scrollYProgress, [TIMELINE.connectionSetup.visible[0], TIMELINE.connectionSetup.visible[0] + 0.03, TIMELINE.connectionSetup.visible[1] - 0.02, TIMELINE.connectionSetup.visible[1]], ['none', 'auto', 'auto', 'none']);
  const subtitle2Opacity = useTransform(scrollYProgress, [TIMELINE.connectionSetup.slideIn[0], TIMELINE.connectionSetup.slideIn[1], TIMELINE.connectionSetup.visible[1] - 0.02, TIMELINE.connectionSetup.visible[1]], [0, 1, 1, 0]);
  const subtitle2Y = useTransform(scrollYProgress, [TIMELINE.connectionSetup.slideIn[0], TIMELINE.connectionSetup.slideIn[1]], ['24vh', '21vh']);
  // === CONTENT 1C: Security Gateway ===
  const gatewayY = useTransform(scrollYProgress, [TIMELINE.security.slideIn[0], TIMELINE.security.slideIn[1], TIMELINE.security.visible[1] - 0.04], ['80vh', '26vh', '26vh']);
  const gatewayOpacity = useTransform(scrollYProgress, [TIMELINE.security.visible[0], TIMELINE.security.visible[0] + 0.03, TIMELINE.security.visible[1] - 0.04, TIMELINE.security.visible[1]], [0, 1, 1, 0]);
  const subtitle3Opacity = useTransform(scrollYProgress, [TIMELINE.security.slideIn[0], TIMELINE.security.slideIn[1], TIMELINE.security.visible[1] - 0.04, TIMELINE.security.visible[1]], [0, 1, 1, 0]);
  const subtitle3Y = useTransform(scrollYProgress, [TIMELINE.security.slideIn[0], TIMELINE.security.slideIn[1]], ['24vh', '21vh']);
  // === SECTION 2: Work Through Chat ===
  const sectionChatY = useTransform(scrollYProgress, [TIMELINE.chat.slideIn[0], TIMELINE.chat.slideIn[1]], ['100vh', '0vh']);
  const chatSubtitleOpacity = useTransform(scrollYProgress, [TIMELINE.chat.visible[0], TIMELINE.chat.visible[0] + 0.02, TIMELINE.chat.visible[1] - 0.03, TIMELINE.chat.visible[1]], [0, 1, 1, 0]);
  const chatSubtitleY = useTransform(scrollYProgress, [TIMELINE.chat.visible[0], TIMELINE.chat.visible[0] + 0.02], ['24vh', '21vh']);
  const chatContentY = useTransform(scrollYProgress, [TIMELINE.chat.visible[0], TIMELINE.chat.visible[0] + 0.02, TIMELINE.chat.visible[1] - 0.01], ['80vh', '31vh', '31vh']);
  const chatContentOpacity = useTransform(scrollYProgress, [TIMELINE.chat.visible[0], TIMELINE.chat.visible[0] + 0.02, TIMELINE.chat.visible[1] - 0.03, TIMELINE.chat.visible[1]], [0, 1, 1, 0]);
  // === SECTION 3: Forget Nothing ===
  const section2Y = useTransform(scrollYProgress, [TIMELINE.forget.slideIn[0], TIMELINE.forget.slideIn[1]], ['100vh', '0vh']);
  // Helper for 4 sub-phases
  const fStep = (TIMELINE.forget.visible[1] - TIMELINE.forget.visible[0]) / 13;
  const f = (n: number) => TIMELINE.forget.visible[0] + fStep * n;
  // Sub 1
  const forgetSub1Opacity = useTransform(scrollYProgress, [f(0), f(1), f(2), f(4)], [0, 1, 1, 0]);
  const forgetSub1Y = useTransform(scrollYProgress, [f(0), f(1)], ['24vh', '21vh']);
  const forgetContent1Y = useTransform(scrollYProgress, [f(0), f(1), f(3)], ['80vh', '30vh', '30vh']);
  const forgetContent1Opacity = useTransform(scrollYProgress, [f(0), f(1), f(2), f(4)], [0, 1, 1, 0]);
  // Sub 2
  const forgetSub2Opacity = useTransform(scrollYProgress, [f(2), f(4), f(5), f(7)], [0, 1, 1, 0]);
  const forgetSub2Y = useTransform(scrollYProgress, [f(2), f(4)], ['24vh', '21vh']);
  const forgetContent2Y = useTransform(scrollYProgress, [f(2), f(4), f(6)], ['80vh', '30vh', '30vh']);
  const forgetContent2Opacity = useTransform(scrollYProgress, [f(2), f(4), f(5), f(7)], [0, 1, 1, 0]);
  // Sub 3
  const forgetSub3Opacity = useTransform(scrollYProgress, [f(5), f(7), f(8), f(10)], [0, 1, 1, 0]);
  const forgetSub3Y = useTransform(scrollYProgress, [f(5), f(7)], ['24vh', '21vh']);
  const forgetContent3Y = useTransform(scrollYProgress, [f(5), f(7), f(9)], ['80vh', '29vh', '29vh']);
  const forgetContent3Opacity = useTransform(scrollYProgress, [f(5), f(7), f(8), f(10)], [0, 1, 1, 0]);
  const forgetContent3PointerEvents = useTransform(scrollYProgress, [f(5), f(7), f(8), f(10)], ['none', 'auto', 'auto', 'none']);
  // Sub 4
  const forgetSub4Opacity = useTransform(scrollYProgress, [f(8), f(10), f(11), f(13)], [0, 1, 1, 0]);
  const forgetSub4Y = useTransform(scrollYProgress, [f(8), f(10)], ['24vh', '21vh']);
  const forgetContent4Y = useTransform(scrollYProgress, [f(8), f(10), f(12)], ['80vh', '29vh', '29vh']);
  const forgetContent4Opacity = useTransform(scrollYProgress, [f(8), f(10), f(11), f(13)], [0, 1, 1, 0]);
  // === SECTION 4: Do Anything ===
  const section3Y = useTransform(scrollYProgress, [TIMELINE.doAnything.slideIn[0], TIMELINE.doAnything.slideIn[1]], ['100vh', '0vh']);
  const content3Y = useTransform(scrollYProgress, [TIMELINE.doAnything.visible[0], TIMELINE.doAnything.visible[1]], ['30vh', '5vh']);
  const scrollToProgress = (progress: number) => {
    if (containerRef.current) {
      const containerHeight = containerRef.current.scrollHeight;
      const containerTop = containerRef.current.offsetTop;
      window.scrollTo({
        top: containerTop + containerHeight * progress,
        behavior: 'smooth'
      });
    }
  };
  return <div ref={containerRef} className="relative h-[1400vh] bg-[#0a0a0a]" data-id="element-315">
      <div className="sticky top-0 h-screen w-full overflow-hidden" data-id="element-316">
        {/* Scroll hint chevron */}
        <motion.div style={{
        opacity: chevronOpacity
      }} className="absolute bottom-8 left-1/2 -translate-x-1/2 z-30 flex flex-col items-center gap-1 cursor-pointer hover:!opacity-100" onClick={() => {
        if (containerRef.current) {
          const scrollTarget = containerRef.current.offsetTop + window.innerHeight * 1.2;
          window.scrollTo({
            top: scrollTarget,
            behavior: 'smooth'
          });
        }
      }} data-id="element-317">
          <motion.div animate={{
          y: [0, 10, 0]
        }} transition={{
          duration: 1.5,
          repeat: Infinity,
          ease: 'easeInOut'
        }} data-id="element-318">
            <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-neutral-500" data-id="element-319">
              <path d="M6 9l6 6 6-6" data-id="element-320" />
            </svg>
          </motion.div>
        </motion.div>

        {/* SECTION 1 & INTRO */}
        <div className="absolute inset-0 z-10" data-id="element-321">
          {/* Intro Titles that fade out */}
          <motion.div style={{
          y: title2IntroY,
          opacity: title2IntroOpacity
        }} className="absolute w-full text-center" data-id="element-322">
            <h1 className="text-4xl md:text-7xl font-bold text-neutral-500 font-title cursor-pointer hover:text-neutral-400 transition-colors" onClick={() => scrollToProgress(TIMELINE.forget.slideIn[0] + 0.02)} data-id="element-323">
              Forget Nothing
            </h1>
          </motion.div>
          <motion.div style={{
          y: title3IntroY,
          opacity: title3IntroOpacity
        }} className="absolute w-full text-center" data-id="element-324">
            <h1 className="text-4xl md:text-7xl font-bold text-neutral-700 font-title cursor-pointer hover:text-neutral-600 transition-colors" onClick={() => scrollToProgress(TIMELINE.doAnything.slideIn[1])} data-id="element-325">
              Do Anything
            </h1>
          </motion.div>

          {/* Morphing Title 1 */}
          <motion.div style={{
          y: title1Y,
          scale: title1Scale
        }} className="absolute w-full text-center origin-top" data-id="element-326">
            <h1 className="text-4xl md:text-7xl font-bold text-white font-title cursor-pointer hover:text-neutral-200 transition-colors" onClick={() => scrollToProgress(TIMELINE.brain.start)} data-id="element-327">
              Connect Everything
            </h1>
          </motion.div>

          {/* Subheading 1 */}
          <motion.div style={{
          y: subtitleY,
          opacity: subtitleOpacity
        }} className="absolute w-full text-center" data-id="element-328">
            <p className="text-lg md:text-3xl text-neutral-400 leading-snug" data-id="element-329">
              Messaging, Analytics, Databases, Tools, Documents
            </p>
          </motion.div>

          {/* Subheading 2 */}
          <motion.div style={{
          y: subtitle2Y,
          opacity: subtitle2Opacity
        }} className="absolute w-full text-center" data-id="element-330">
            <p className="text-lg md:text-3xl text-neutral-400 leading-snug" data-id="element-331">
              No technical knowledge required
            </p>
          </motion.div>

          {/* Subheading 3 */}
          <motion.div style={{
          y: subtitle3Y,
          opacity: subtitle3Opacity
        }} className="absolute w-full text-center" data-id="element-332">
            <p className="text-lg md:text-3xl text-neutral-400 leading-snug" data-id="element-333">
              Safety built in
            </p>
          </motion.div>

          {/* Content 1A: Brain Animation */}
          <motion.div style={{
          y: content1Y,
          opacity: content1Opacity
        }} className="absolute w-full px-6" data-id="element-334">
            <ConnectAnimation scrollYProgress={scrollYProgress} sectionStart={TIMELINE.brain.start} sectionEnd={TIMELINE.brain.end} data-id="element-335" />
          </motion.div>

          {/* Content 1B: Product GIF */}
          <motion.div style={{
          y: securityY,
          opacity: securityOpacity,
          pointerEvents: securityPointerEvents as any
        }} className="absolute w-full px-6 pointer-events-auto" data-id="element-336">
            <ConnectionSetup scrollYProgress={scrollYProgress} contentStart={TIMELINE.connectionSetup.content[0]} contentEnd={TIMELINE.connectionSetup.content[1]} data-id="element-337" />
          </motion.div>

          {/* Content 1C: Security Gateway */}
          <motion.div style={{
          y: gatewayY,
          opacity: gatewayOpacity
        }} className="absolute w-full px-6" data-id="element-338">
            <SecurityProxy scrollYProgress={scrollYProgress} contentStart={TIMELINE.security.content[0]} contentEnd={TIMELINE.security.content[1]} data-id="element-339" />
          </motion.div>
        </div>

        {/* SECTION 2: Work Through Chat */}
        <motion.div style={{
        y: sectionChatY
      }} className="absolute inset-0 bg-[#0a0a0a] z-20 shadow-[0_-20px_50px_rgba(0,0,0,0.8)]" data-id="element-340">
          <div className="absolute w-full flex justify-center" style={{
          top: '15vh'
        }} data-id="element-341">
            <div className="origin-top" style={{
            transform: 'scale(0.6)'
          }} data-id="element-342">
              <h1 className="text-4xl md:text-7xl font-bold text-white font-title" data-id="element-343">
                Work Through Chat
              </h1>
            </div>
          </div>
          {/* Tagline */}
          <motion.div style={{
          y: chatSubtitleY,
          opacity: chatSubtitleOpacity
        }} className="absolute w-full text-center px-6" data-id="element-344">
            <p className="text-lg md:text-3xl text-neutral-400 max-w-2xl mx-auto leading-snug" data-id="element-345">
              With systems connected, ask Giga to help you get your work done
              much faster
            </p>
          </motion.div>
          {/* Chat Examples */}
          <motion.div style={{
          y: chatContentY,
          opacity: chatContentOpacity
        }} className="absolute w-full px-6" data-id="element-346">
            <ChatExamples scrollYProgress={scrollYProgress} scrollStart={TIMELINE.chat.content[0]} scrollEnd={TIMELINE.chat.content[1]} data-id="element-347" />
          </motion.div>
        </motion.div>

        {/* SECTION 3: Forget Nothing */}
        <motion.div style={{
        y: section2Y
      }} className="absolute inset-0 bg-[#0a0a0a] z-30 shadow-[0_-20px_50px_rgba(0,0,0,0.8)]" data-id="element-348">
          <div className="absolute w-full flex justify-center" style={{
          top: '15vh'
        }} data-id="element-349">
            <div className="origin-top" style={{
            transform: 'scale(0.6)'
          }} data-id="element-350">
              <h1 className="text-4xl md:text-7xl font-bold text-white font-title" data-id="element-351">
                Forget Nothing
              </h1>
            </div>
          </div>

          {/* Sub 1 */}
          <motion.div style={{
          y: forgetSub1Y,
          opacity: forgetSub1Opacity
        }} className="absolute w-full text-center px-6" data-id="element-352">
            <p className="text-lg md:text-3xl text-neutral-400 max-w-2xl mx-auto leading-snug" data-id="element-353">
              With the team plugged in to Giga, knowledge accumulates
              organically over time
            </p>
          </motion.div>
          <motion.div style={{
          y: forgetContent1Y,
          opacity: forgetContent1Opacity
        }} className="absolute w-full px-6" data-id="element-354">
            <MemoryCenter data-id="element-355" />
          </motion.div>

          {/* Sub 2 */}
          <motion.div style={{
          y: forgetSub2Y,
          opacity: forgetSub2Opacity
        }} className="absolute w-full text-center px-6" data-id="element-356">
            <p className="text-lg md:text-3xl text-neutral-400 max-w-2xl mx-auto leading-snug" data-id="element-357">
              <strong className="text-white font-semibold" data-id="element-358">
                Centralized Chats
              </strong>{' '}
              puts all sessions from you and your team in one place, with
              powerful sharing
            </p>
          </motion.div>
          <motion.div style={{
          y: forgetContent2Y,
          opacity: forgetContent2Opacity
        }} className="absolute w-full px-6" data-id="element-359">
            <ChatManagement data-id="element-360" />
          </motion.div>

          {/* Sub 3 */}
          <motion.div style={{
          y: forgetSub3Y,
          opacity: forgetSub3Opacity
        }} className="absolute w-full text-center px-6" data-id="element-361">
            <p className="text-lg md:text-3xl text-neutral-400 max-w-2xl mx-auto leading-snug" data-id="element-362">
              <strong className="text-white font-semibold" data-id="element-363">Recaps</strong>{' '}
              distil long conversations down to one-line-per-exchange
            </p>
          </motion.div>
          <motion.div style={{
          y: forgetContent3Y,
          opacity: forgetContent3Opacity,
          pointerEvents: forgetContent3PointerEvents
        }} className="absolute w-full px-6 z-10" data-id="element-364">
            <RecapsFeed data-id="element-365" />
          </motion.div>

          {/* Sub 4 */}
          <motion.div style={{
          y: forgetSub4Y,
          opacity: forgetSub4Opacity
        }} className="absolute w-full text-center px-6" data-id="element-366">
            <p className="text-lg md:text-3xl text-neutral-400 max-w-2xl mx-auto leading-snug" data-id="element-367">
              <strong className="text-white font-semibold" data-id="element-368">Journeys</strong>{' '}
              keep track of important initiatives and explorations over time
            </p>
          </motion.div>
          <motion.div style={{
          y: forgetContent4Y,
          opacity: forgetContent4Opacity
        }} className="absolute w-full px-6" data-id="element-369">
            <div className="w-full max-w-[800px] mx-auto aspect-video rounded-2xl border border-neutral-800 bg-neutral-900/40 flex items-center justify-center" data-id="element-370">
              <div className="text-center" data-id="element-371">
                <div className="w-16 h-16 rounded-2xl bg-neutral-800/80 border border-neutral-700 flex items-center justify-center mx-auto mb-4" data-id="element-372">
                  <div className="w-6 h-6 rounded bg-neutral-600 animate-pulse" data-id="element-373" />
                </div>
                <p className="text-neutral-500 text-sm" data-id="element-374">
                  Journeys UI Placeholder
                </p>
              </div>
            </div>
          </motion.div>
        </motion.div>

        {/* SECTION 4: Do Anything */}
        <motion.div style={{
        y: section3Y
      }} className="absolute inset-0 bg-[#0a0a0a] z-40 shadow-[0_-20px_50px_rgba(0,0,0,0.8)]" data-id="element-375">
          <div className="absolute w-full flex justify-center" style={{
          top: '15vh'
        }} data-id="element-376">
            <div className="origin-top" style={{
            transform: 'scale(0.6)'
          }} data-id="element-377">
              <h1 className="text-4xl md:text-7xl font-bold text-white font-title" data-id="element-378">
                Do Anything
              </h1>
            </div>
          </div>
          <motion.div style={{
          y: content3Y
        }} className="absolute w-full px-6" data-id="element-379">
            <DoAnythingCarousel data-id="element-380" />
          </motion.div>
        </motion.div>
      </div>
    </div>;
}